import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fmtDate, fmtMoney, fmtErr, todayLocal } from "../../api";
import Layout from "../../components/Layout";
import { MapPin, Warning, CheckCircle, Clock, Snowflake } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLE = {
  scheduled: "bg-muted text-muted-foreground",
  assigned: "bg-secondary text-secondary-foreground",
  in_progress: "bg-[hsl(196,72%,56%)] text-[hsl(240,10%,10%)]",
  blocked: "bg-[hsl(24,90%,42%)] text-white",
  completed: "bg-primary text-primary-foreground",
  invoiced: "bg-primary text-primary-foreground",
  paid: "bg-primary text-primary-foreground",
};

export const StatusBadge = ({ s }) => (
  <span data-testid={`status-${s}`} className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[s] || "bg-muted"}`}>{s.replace("_", " ")}</span>
);

export function getGeo() {
  return new Promise((res) => {
    if (!navigator.geolocation) return res(null);
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => res(null),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
    );
  });
}

export function getGeoAccurate(maxMs = 10000, targetM = 15) {
  // Stage A: re-sample with getCurrentPosition (no watchPosition) until accuracy beats
  // targetM or the budget runs out; resolve with the best fix seen. Permission denial
  // stops immediately; other errors retry on a short delay until the deadline.
  return new Promise((res) => {
    if (!navigator.geolocation) return res(null);
    let best = null;
    let done = false;
    const finish = () => { if (!done) { done = true; clearTimeout(timer); res(best); } };
    const timer = setTimeout(finish, maxMs);
    const sample = () => {
      if (done) return;
      navigator.geolocation.getCurrentPosition(
        (p) => {
          const fix = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy,
                        fix_time: new Date(p.timestamp).toISOString() };
          if (!best || (fix.accuracy ?? 1e9) < (best.accuracy ?? 1e9)) best = fix;
          if (fix.accuracy != null && fix.accuracy <= targetM) finish();
          else sample();
        },
        (e) => { if (e && e.code === 1) finish(); else if (!done) setTimeout(sample, 700); },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 3000 }
      );
    };
    sample();
  });
}

export const signalLabel = (acc) => (acc == null ? null : acc <= 15 ? "good" : acc <= 50 ? "fair" : "poor");

export const km = (a, b, c, d) => {
  const R = 6371, p = Math.PI / 180;
  const h = Math.sin(((c - a) * p) / 2) ** 2 + Math.cos(a * p) * Math.cos(c * p) * Math.sin(((d - b) * p) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const ROW_ANIM = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

export const VisitRow = ({ v, order, showDate }) => (
  <Link to={`/visits/${v.id}`} data-testid={`visit-row-${v.id}`}>
    <motion.div {...ROW_ANIM} className="card-flat flex items-center justify-between p-4 transition-colors hover:bg-muted/50">
      {order && <span data-testid={`route-order-${v.id}`} className="mr-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-primary-foreground">{order}</span>}
      <div className="min-w-0">
        {showDate && <p className="text-xs font-bold text-muted-foreground" data-testid={`visit-date-${v.id}`}>{fmtDate(v.date)}</p>}
        <p className="truncate font-semibold">{v.property_name}</p>
        {v.address && <p className="truncate text-xs text-muted-foreground" data-testid={`visit-address-${v.id}`}>{v.address}</p>}
        {v.status !== "completed" && v.last_completed_date && (
          <p className="mt-1 text-xs font-semibold" data-testid={`last-done-${v.id}`}>
            {v.cadence_status === "alert" && <span className="mr-1 rounded-full bg-destructive/15 px-2 py-0.5 font-bold text-destructive">Overdue</span>}
            {v.cadence_status === "warn" && <span className="mr-1 rounded-full bg-[hsl(43,74%,50%)]/20 px-2 py-0.5 font-bold text-[hsl(43,64%,35%)]">Due</span>}
            <span className="text-muted-foreground">Last done {fmtDate(v.last_completed_date)} · {v.days_since_last}d ago</span>
          </p>
        )}
        <p className="truncate text-sm text-muted-foreground">{v.owner_name} · {v.services.map((s) => s.name).join(", ") || "Add-on visit"}</p>
        {v.instructions?.length > 0 && <p className="mt-1 text-xs font-semibold text-[hsl(43,74%,40%)]">+{v.instructions.length} special instruction{v.instructions.length > 1 ? "s" : ""}</p>}
        {v.unassigned && <p className="mt-1 flex items-center gap-1 text-xs font-bold text-destructive"><Warning size={14} weight="bold" /> Unassigned — needs action</p>}
        {v.status === "blocked" && <p className="mt-1 flex items-center gap-1 text-xs font-bold text-[hsl(24,90%,42%)]" data-testid={`blocked-line-${v.id}`}><Warning size={14} weight="bold" /> Blocked{v.blocked_reason ? `: ${v.blocked_reason}` : ""}{v.blocked_note ? ` — ${v.blocked_note}` : ""}</p>}
        {v.assignee_name && <p className="mt-1 text-xs text-muted-foreground">→ {v.assignee_name}</p>}
        {["completed", "invoiced", "paid"].includes(v.status) && v.completed_at && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid={`done-by-${v.id}`}>Done {fmtDate(v.completed_at.slice(0, 10))}{v.assignee_name ? ` by ${v.assignee_name.replace("You (operator)", "you")}` : ""}</p>
        )}
      </div>
      <StatusBadge s={v.status} />
    </motion.div>
  </Link>
);

export default function Dashboard() {
  const [visits, setVisits] = useState(null);
  const [nearby, setNearby] = useState(null);
  const [wx, setWx] = useState(null);
  const [q, setQ] = useState("");
  const [route, setRoute] = useState(null);
  const [filter, setFilter] = useState("all"); // all | open | assigned | working | done — daily triage
  const [snowEvent, setSnowEvent] = useState(null);
  const [passSel, setPassSel] = useState(null); // null = all covered properties selected

  const load = () => api.get("/visits").then((r) => setVisits(r.data)).catch(() => toast.error("Could not load visits"));
  useEffect(() => { api.get("/settings").then((r) => setEtransferSet(!!r.data.etransfer_email)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/email-log").then((r) => {
      const cutoff = Date.now() - 24 * 3600 * 1000;
      setEmailRejected((r.data || []).some((e) => e.status === "rejected" && Date.parse(e.created_at) > cutoff));
    }).catch(() => {});
  }, []);
  const loadSnow = () => api.get("/snow/events").then((r) => setSnowEvent(r.data.find((e) => !e.closed_at) || null)).catch(() => {});
  useEffect(() => {
    load();
    loadSnow();
    api.get("/weather/forecast").then((r) => setWx(r.data)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap + polling interval by design
  }, []);

  useEffect(() => {
    getGeo().then((g) => {
      if (!g || !visits) return;
      const hit = visits.find((v) => v.date === todayLocal() && ["scheduled", "assigned"].includes(v.status) && v.lat && km(g.lat, g.lng, v.lat, v.lng) <= 0.3);
      if (hit) setNearby(hit);
    });
  }, [visits]);

  const quickCheckin = async () => {
    const g = await getGeo();
    await api.post(`/visits/${nearby.id}/checkin`, g || {});
    toast.success("Checked in");
    setNearby(null);
    load();
  };

  const storm = async () => {
    try {
      const r = await api.post("/storm/broadcast", {});
      toast.success(r.data.visits_offered
        ? `Broadcast: ${r.data.visits_offered} job${r.data.visits_offered > 1 ? "s" : ""} offered to ${r.data.subs_notified} crew member${r.data.subs_notified > 1 ? "s" : ""}`
        : "No unassigned jobs today");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const [snowAckOpen, setSnowAckOpen] = useState(false);
  const [etransferSet, setEtransferSet] = useState(true);
  const [emailRejected, setEmailRejected] = useState(false);
  const pendingSnowSrc = useRef("manual");

  const confirmSnowAck = async () => {
    try {
      await api.patch("/settings", { snow_photo_min: 0, snow_photo_ack: true });
    } catch (e) { toast.error(fmtErr(e)); return; }
    setSnowAckOpen(false);
    startSnowEvent(pendingSnowSrc.current);
  };

  const startSnowEvent = async (source = "manual") => {
    try {
      const s = await api.get("/settings").then((r) => r.data).catch(() => null);
      if (s && Number(s.snow_photo_min ?? 0) === 0 && !s.snow_photo_ack_at) {
        pendingSnowSrc.current = source;
        return setSnowAckOpen(true);  // insurance warning shown once before the first no-photo event
      }
      const r = await api.post("/snow/events", { source });
      toast.success(`Snow event started — ${r.data.visits_created} visit${r.data.visits_created === 1 ? "" : "s"} created for covered properties`);
      loadSnow(); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addSnowPass = async () => {
    try {
      const all = snowEvent.property_ids || [];
      const ids = passSel === null ? all : all.filter((pid) => passSel[pid] !== false);
      const r = await api.post(`/snow/events/${snowEvent.id}/passes`, ids.length && ids.length < all.length ? { property_ids: ids } : {});
      toast.success(`Pass ${r.data.pass_number}: ${r.data.visits_created} more visit${r.data.visits_created === 1 ? "" : "s"} created`);
      setPassSel(null); loadSnow(); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const closeSnowEvent = async () => {
    try {
      await api.post(`/snow/events/${snowEvent.id}/close`);
      toast.success("Snow event closed — visits stay on the board");
      setSnowEvent(null); setPassSel(null);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reviewList = (visits || []).filter((v) => v.event_id && v.route_match && ["out_of_range", "no_gps"].includes(v.route_match.status));

  const routeConfirm = async (vid) => {
    try {
      await api.post(`/visits/${vid}/route-confirm`);
      toast.success("Confirmed — marked verified by your confirmation");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const delayToday = async () => {
    try {
      const r = await api.post("/visits/delay-day", { days: 1, reason: "rain" });
      toast.success(`Rain delay: ${r.data.delayed} job${r.data.delayed === 1 ? "" : "s"} moved to ${r.data.to}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const suggestRoute = async () => {
    if (route) { setRoute(null); return; }
    const g = await getGeo();
    const pool = todays.filter((v) => v.lat && v.lng);
    const noGeo = todays.filter((v) => !v.lat || !v.lng);
    let cur = g || (pool[0] && { lat: pool[0].lat, lng: pool[0].lng });
    const ordered = [];
    const remaining = [...pool];
    while (remaining.length && cur) {
      remaining.sort((a, b) => km(cur.lat, cur.lng, a.lat, a.lng) - km(cur.lat, cur.lng, b.lat, b.lng));
      const next = remaining.shift();
      ordered.push(next);
      cur = { lat: next.lat, lng: next.lng };
    }
    setRoute([...ordered, ...noGeo].map((v) => v.id));
    if (pool.length) toast.success("Route order suggested — nearest stop first");
    else toast("No map locations on today's jobs yet — save the property address to geocode it");
  };

  if (!visits) return <Layout title="Today"><p className="text-muted-foreground">Loading…</p></Layout>;

  const today = todayLocal();
  const match = (v) => !q || [v.property_name, v.address, v.owner_name, v.assignee_name, (v.services || []).map((s) => s.name).join(" ")].join(" ").toLowerCase().includes(q.toLowerCase());
  const current = visits.find((v) => v.status === "in_progress");
  const todays = visits.filter((v) => v.date === today && !["in_progress", "cancelled"].includes(v.status)).filter(match);
  const todaysSorted = route ? route.map((id) => todays.find((v) => v.id === id)).filter(Boolean) : todays;
  const openToday = todays.filter((v) => v.status === "scheduled" && !v.assignee_id).length;
  const upcoming = visits.filter((v) => v.date > today && v.status !== "cancelled").filter(match);
  // past-week visits ride along in the API window — only still-open ones matter; done ones are history
  const pastOpen = visits.filter((v) => v.date < today && ["scheduled", "assigned", "in_progress", "blocked"].includes(v.status)).filter(match);
  const countable = visits.filter((v) => v.status !== "cancelled");  // §13.12: done visits stay countable + searchable within the payload window
  const byDay = upcoming.reduce((m, v) => ((m[v.date] = m[v.date] || []).push(v), m), {});

  const TRIAGE_FILTERS = [
    { k: "all", label: "All" },
    { k: "open", label: "Needs assignment" },
    { k: "assigned", label: "Assigned" },
    { k: "working", label: "In progress" },
    { k: "blocked", label: "Blocked" },
    { k: "done", label: "Done" },
  ];
  const bucket = (v, k) => {
    if (k === "open") return v.status === "scheduled" && !v.assignee_id;
    if (k === "assigned") return v.status === "assigned";
    if (k === "working") return v.status === "in_progress";
    if (k === "blocked") return v.status === "blocked";
    if (k === "done") return ["completed", "invoiced", "paid"].includes(v.status);
    return true;
  };
  const triageMatch = (v) => bucket(v, filter);
  const triageCount = (k) => countable.filter(match).filter((v) => bucket(v, k)).length;
  const triage = filter === "all" ? null
    : countable.filter(match).filter(triageMatch).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  return (
    <Layout title="Today">
      {!etransferSet && (
        <p className="mb-4 text-sm text-muted-foreground" data-testid="etransfer-prompt">
          Add your e-Transfer email in <Link to="/settings/invoicing" className="font-bold text-primary underline">Settings → Invoicing</Link>
        </p>
      )}
      {emailRejected && (
        <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm font-semibold text-destructive" data-testid="email-rejected-banner">
          Some emails didn't send. Check <Link to="/settings/messages" className="font-bold underline">Settings → Messages</Link>
        </div>
      )}
      {wx?.offers_snow !== false && wx?.snow_alert && (
        <div className="card-flat mb-4 border-l-4 border-l-[hsl(196,72%,56%)] p-4" data-testid="storm-watch-banner">
          <p className="font-semibold">Storm watch: {Math.round(wx.snow_alert.snow_cm)} cm snow expected {fmtDate(wx.snow_alert.date)}</p>
          <p className="text-sm text-muted-foreground">Line up your crew now — broadcast mode blasts the day's open jobs to everyone.</p>
          {!snowEvent && (
            <button data-testid="storm-start-event-btn" onClick={() => startSnowEvent("storm_watch")}
              className="mt-3 h-12 w-full rounded-md bg-[hsl(196,72%,56%)] text-sm font-bold text-[hsl(240,10%,10%)]">Start snow event</button>
          )}
        </div>
      )}
      {wx?.offers_lawn !== false && wx?.rain_alert && wx.rain_alert.date === today && openToday > 0 && (
        <div className="card-flat mb-4 border-l-4 border-l-[hsl(43,74%,50%)] p-4" data-testid="rain-watch-banner">
          <p className="font-semibold">Rain expected today — {Math.round(wx.rain_alert.rain_mm)} mm ({wx.rain_alert.precip_pct}% chance)</p>
          <p className="text-sm text-muted-foreground">You can't cut wet grass. Push today's {openToday} open job{openToday > 1 ? "s" : ""} to tomorrow?</p>
          <button data-testid="rain-delay-today-btn" onClick={delayToday} className="mt-3 h-12 w-full rounded-md bg-primary font-bold text-primary-foreground">Delay today's jobs → tomorrow</button>
        </div>
      )}
      <button data-testid="storm-broadcast-btn" onClick={storm}
        className="mb-6 flex h-14 w-full items-center justify-center gap-2 rounded-md border-2 border-[hsl(196,72%,56%)] font-bold text-[hsl(196,72%,46%)] transition-colors hover:bg-[hsl(196,72%,56%)]/10">
        <Warning size={20} weight="bold" /> Broadcast mode — blast today's jobs to all crew{openToday ? ` (${openToday})` : ""}
      </button>
      {wx?.offers_snow !== false && !snowEvent && (
        <button data-testid="start-snow-event-btn" onClick={() => startSnowEvent("manual")}
          className="mb-6 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-[hsl(196,72%,56%)] font-bold text-[hsl(240,10%,10%)]">
          <Snowflake size={20} weight="bold" /> Start snow event
        </button>
      )}
      {wx?.offers_snow !== false && snowEvent && (
        <div className="card-flat mb-6 border-l-4 border-l-[hsl(196,72%,56%)] p-4" data-testid="snow-event-card">
          <p className="font-semibold">Snow event open — {snowEvent.passes} pass{snowEvent.passes > 1 ? "es" : ""}{snowEvent.snowfall_cm ? ` · ${snowEvent.snowfall_cm} cm` : ""}</p>
          <p className="text-sm text-muted-foreground">Started {fmtDate((snowEvent.started_at || "").slice(0, 10))}. Its visits flow through normal dispatch below.</p>
          <p className="mt-3 text-xs font-bold text-muted-foreground">Add another pass for:</p>
          <div className="mt-1 space-y-2">
            {(snowEvent.property_ids || []).map((pid) => {
              const pv = visits.find((x) => x.property_id === pid && x.event_id === snowEvent.id);
              return (
                <label key={pid} className="flex items-center gap-2 text-sm font-semibold" data-testid={`pass-prop-${pid}`}>
                  <input type="checkbox" className="h-5 w-5" checked={passSel === null || passSel[pid] !== false}
                    onChange={(e) => setPassSel({ ...(passSel || Object.fromEntries((snowEvent.property_ids || []).map((x) => [x, true]))), [pid]: e.target.checked })} />
                  {pv?.property_name || "Property"}
                </label>
              );
            })}
          </div>
          <button data-testid="add-snow-pass-btn" onClick={addSnowPass}
            className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-[hsl(196,72%,56%)] font-bold text-[hsl(240,10%,10%)]">Add another pass</button>
          <Link to="/snow-route" data-testid="open-snow-route-btn"
            className="mt-2 flex h-14 w-full items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">Open snow route</Link>
          <button data-testid="close-snow-event-btn" onClick={closeSnowEvent}
            className="mt-2 h-14 w-full rounded-md border border-border text-sm font-bold">Close event</button>
        </div>
      )}
      {reviewList.length > 0 && (
        <section className="mb-6" data-testid="review-list">
          <p className="label-caps mb-2 text-destructive">Review — location checks ({reviewList.length})</p>
          <div className="space-y-2">
            {reviewList.map((v) => (
              <div key={v.id} className="card-flat space-y-2 border-l-4 border-l-[hsl(24,90%,42%)] p-4" data-testid={`review-item-${v.id}`}>
                <p className="font-semibold">{v.property_name} <span className="text-xs font-bold text-muted-foreground">· {v.owner_name}</span></p>
                <p className="text-xs font-bold text-[hsl(24,90%,42%)]">{v.route_match.status === "no_gps" ? "No GPS fix on record" : "Out of range — not clearly at the dispatched property"}</p>
                {v.route_match.confirmable && (
                  <div>
                    <p className="text-xs text-muted-foreground">Were you at {v.property_name} — {v.owner_name}?</p>
                    <button data-testid={`route-confirm-${v.id}`} onClick={() => routeConfirm(v.id)}
                      className="mt-1 h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Yes, I was there</button>
                  </div>
                )}
                <Link to={`/visits/${v.id}`} data-testid={`review-open-${v.id}`} className="block text-xs font-bold text-primary underline">Open job sheet</Link>
              </div>
            ))}
          </div>
        </section>
      )}
      {snowAckOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="snow-photo-ack-dialog-dash">
          <div className="card-flat w-full max-w-md space-y-4 bg-background p-6">
            <p className="font-heading text-lg font-bold">Snow stops without photos</p>
            <p className="text-sm" data-testid="snow-photo-ack-text-dash">Without photos, your visit records rely on GPS and map snapshots only. That is weaker evidence in a slip-and-fall claim and may affect your insurance defence or premium. Check with your broker.</p>
            <button type="button" data-testid="snow-photo-ack-confirm-dash" onClick={confirmSnowAck}
              className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">I understand — start the event</button>
            <button type="button" data-testid="snow-photo-ack-cancel-dash" onClick={() => setSnowAckOpen(false)}
              className="h-12 w-full rounded-md border-2 border-border text-sm font-bold">Cancel</button>
          </div>
        </div>
      )}
      <input data-testid="search-input" className="field-input mb-6 w-full" placeholder="Search property, owner, address, crew…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="mb-6 flex flex-wrap gap-2" data-testid="triage-filters">
        {TRIAGE_FILTERS.map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)}
            className={`h-10 rounded-full border px-4 text-xs font-bold transition-colors ${filter === f.k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
            {f.label} ({triageCount(f.k)})
          </button>
        ))}
      </div>
      {triage && (
        <section className="mb-8" data-testid="triage-list">
          {triage.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">Nothing in this bucket.</p>}
          <div className="space-y-2">
            {triage.map((v) => <VisitRow key={v.id} v={v} showDate />)}
          </div>
          {filter === "done" && (
            <Link to="/history" data-testid="history-link" className="mt-3 inline-block text-xs font-bold text-primary underline">Older than 7 days → Job history</Link>
          )}
        </section>
      )}
      {nearby && (
        <div className="card-flat mb-4 flex items-center justify-between gap-3 border-primary p-4" data-testid="nearby-banner">
          <div className="flex items-center gap-2 text-sm font-semibold"><MapPin size={20} weight="bold" className="text-primary" /> You're at {nearby.property_name}</div>
          <button data-testid="nearby-checkin-btn" onClick={quickCheckin} className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Check in</button>
        </div>
      )}
      {current && (
        <div className="mb-8" data-testid="current-job">
          <p className="label-caps mb-2">On the job now</p>
          <Link to={`/visits/${current.id}`}>
            <div className="rounded-md bg-primary p-6 text-primary-foreground">
              <p className="font-heading text-2xl font-bold">{current.property_name}</p>
              <p className="mt-1 text-sm opacity-90 flex items-center gap-1"><Clock size={16} weight="bold" /> Checked in {new Date(current.check_in?.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
            </div>
          </Link>
        </div>
      )}
      {!triage && (
        <>
          {pastOpen.length > 0 && (
            <section className="mb-8" data-testid="past-open-list">
              <p className="label-caps mb-3 text-destructive">Earlier — still open</p>
              <div className="space-y-2">{pastOpen.map((v) => <VisitRow key={v.id} v={v} showDate />)}</div>
            </section>
          )}
          <section className="mb-8">
            <div className="mb-3 flex items-center justify-between">
              <p className="label-caps">Today · {fmtDate(today)}</p>
              {todays.length >= 2 && (
                <button data-testid="suggest-route-btn" onClick={suggestRoute} className="text-xs font-bold text-primary underline">
                  {route ? "Clear route order" : "Suggest route order"}
                </button>
              )}
            </div>
            <div className="space-y-2" data-testid="today-list">
              {todays.length === 0 && !current && <p className="card-flat p-6 text-sm text-muted-foreground">Nothing scheduled today.</p>}
              {todaysSorted.map((v) => <VisitRow key={v.id} v={v} order={route ? route.indexOf(v.id) + 1 : null} />)}
            </div>
          </section>
          {Object.entries(byDay).map(([day, vs]) => (
            <section key={day} className="mb-8">
              <p className="label-caps mb-3">{fmtDate(day)}</p>
              <div className="space-y-2">{vs.map((v) => <VisitRow key={v.id} v={v} />)}</div>
            </section>
          ))}
        </>
      )}
    </Layout>
  );
}
